import { useCallback, useContext, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import cuid2 from '@paralleldrive/cuid2';
import * as atlas from 'azure-maps-control';
import { Check, Edit2, X } from 'react-feather';
import { SelectLocation } from '@trinity/chat-components/components/Search/Location';
import type { Chat } from '@trinity/chat';
import type { HcpSearchResult } from '@trinity/chat';
import type { KolSearchResult } from '@trinity/chat';
import type { LocationItem } from '@trinity/chat';
import { LocationProviderType } from '@trinity/chat';
import { useBrowserLocation } from '@trinity/chat-components/hooks';
import { AzureMap } from '@trinity/chat-components/components/AzureMap';
import { SmartRoutingHcpContext } from './context';
import { OpenMapsButton } from './OpenMapsButton';
import type { SmartRoutingAction } from './SmartRouting.Container';
import { BuildMapLinkParams, LocationDescriptor } from '../../lib';

type Props = {
  state: Chat.QueryState.SmartRouting;
  dispatch: (action: SmartRoutingAction) => void;
};

const getHcpId = (hcp: KolSearchResult | HcpSearchResult): number | null => {
  if ('hcpIdentifier' in hcp && hcp.hcpIdentifier != null) {
    return Number(hcp.hcpIdentifier);
  }
  if ('id' in hcp && typeof hcp.id === 'number') {
    return hcp.id;
  }
  return null;
};

const getHcpAddress = (hcp: KolSearchResult | HcpSearchResult): string => {
  if ('streetAddress' in hcp) {
    // KolSearchResult
    const addressParts = [hcp.streetAddress, hcp.city, hcp.state].filter(Boolean);
    return addressParts.join(', ');
  } else {
    return (hcp as HcpSearchResult).location || '';
  }
};

const getHcpLocationCoords = (hcp: KolSearchResult | HcpSearchResult) => {
  if ('locationCoords' in hcp) {
    return hcp.locationCoords; // KolSearchResult
  } else {
    const h = hcp as HcpSearchResult;
    return h.primaryLocation ? {
      latitude: h.primaryLocation.lat,
      longitude: h.primaryLocation.lon,
    } : null; // HcpSearchResult
  }
};

const getHcpName = (hcp: KolSearchResult | HcpSearchResult): string => {
  return hcp.name; // Both types have name field
};

export const SmartRoutingResult = ({ state, dispatch }: Props) => {
  const mapParams = useMemo(() => {
    if (!state.routingResult) return null;

    const points = [state.routingResult.legs[0].start, ...state.routingResult.legs.map(leg => leg.end)].map<LocationDescriptor>(point => {
      if (point.address) {
        return {
          type: 'address',
          address: point.address,
        };
      } else {
        return {
          type: 'coords',
          latitude: point.latitude,
          longitude: point.longitude,
        };
      }
    });

    const mapsParams: BuildMapLinkParams = {
      origin: points[0],
      destination: points[points.length - 1],
      waypoints: points.slice(1, -1),
    };

    return mapsParams;
  }, [state.routingResult]);

  if (!state.routingResult) {
    return null;
  }

  return (
    <div>
      <EditableRouteLegs state={state} dispatch={dispatch} />
      <RouteResultMap state={state} />
      {mapParams && (
        <OpenMapsButton mapParams={mapParams}>
          <AutoAwesomeIcon style={{ fontSize: 18 }} /> Get Directions
        </OpenMapsButton>
      )}
    </div>
  );
};

type RouteLegProps = {
  ordinal: number;
  name: string;
  location: string;
  distance: string;
};

const RouteLeg = (props: RouteLegProps) => {
  return (
    <Root>
      <Em>{`${props.ordinal}. ${props.name}`}</Em>
      <div>{`-`}</div>
      <div>{props.location}</div>
      <div>{`-`}</div>
      <div>{props.distance}</div>
    </Root>
  );
};

type EditableRouteLegProps = {
  latitude: number;
  longitude: number;
  kolId: number;
  setForm: (data: Omit<LocationItem, 'name'> & { location: string }) => void;
} & RouteLegProps;

const EditableRouteLeg = ({ ordinal, setForm, ...props }: EditableRouteLegProps) => {
  const handleChange = useCallback((data: LocationItem) => {
    setForm({
      latitude: data.latitude,
      longitude: data.longitude,
      location: data.name,
      id: data.id ?? ordinal.toString(),
    });
  }, [
    ordinal,
    setForm,
  ]);

  return (
    <Root>
      <Em>{`${ordinal}. ${props.name}`}</Em>
      <div>
        <SelectLocation
          onChange={handleChange}
          placeholder={props.location}
          value={{
            latitude: props.latitude,
            longitude: props.longitude,
            id: ordinal.toString(),
            name: props.location,
          }} />
      </div>
    </Root>
  );
};

type FormItem = Omit<LocationItem, 'id' | 'name'> & { id?: string; location: string };

const EditableRouteLegs = ({ state, dispatch }: Pick<Props, 'state' | 'dispatch'>) => {
  const { getLocation } = useBrowserLocation({ onError: () => { }, onSuccess: () => { } });
  const [editing, setEditing] = useState(false);
  const { searchResults: hcpResults } = useContext(SmartRoutingHcpContext);

  const buildLeg = useCallback((leg: Chat.QueryState.SmartRouting['routingResult']['legs'][0], ordinal: number) => {
    const kolId = leg.end.kolId;
    // Find the HCP data from the search results
    const hcp = hcpResults.find(h => getHcpId(h) === kolId);

    let location: string;
    if (hcp) {
      // Format address properly from HCP data
      location = getHcpAddress(hcp);
    } else {
      // Fallback to routing result address or coordinates
      location = leg.end.address || `(${leg.end.longitude}, ${leg.end.latitude})`;
    }

    const props = {
      ordinal: ordinal + 1,
      name: hcp?.name || `HCP ${kolId}`,
      location,
      distance: `${leg.distanceMiles.toFixed(1)} miles`,
      latitude: leg.end.latitude,
      longitude: leg.end.longitude,
      kolId,
    };

    return props;
  }, [hcpResults]);

  const legs = useMemo(() => {
    if (!state.routingResult) return [];
    return state.routingResult.legs.map((x, i) => buildLeg(x, i));
  }, [buildLeg, state.routingResult]);

  const getInitialState = useCallback(() => {
    return legs.reduce((acc, x) => ({
      ...acc,
      [x.ordinal]: {
        latitude: x.latitude,
        longitude: x.longitude,
        location: x.location,
        id: null,
      },
    }), {} as Record<number, FormItem>);
  }, [legs]);

  const [startLocation, setStartLocation] = useState<FormItem>(() => {
    if (!state.routingResult) return { latitude: 0, longitude: 0, location: '' };

    const startPoint = state.routingResult.legs[0].start;
    let location: string;

    if (startPoint.kolId) {
      const startHcp = hcpResults.find(h => getHcpId(h) === startPoint.kolId);
      if (startHcp) {
        location = getHcpAddress(startHcp);
      } else {
        location = startPoint.address || `(${startPoint.longitude}, ${startPoint.latitude})`;
      }
    } else {
      const matchingHcp = hcpResults.find(h => {
        const coords = getHcpLocationCoords(h);
        return coords &&
          Math.abs(coords.latitude - startPoint.latitude) < 0.0001 &&
          Math.abs(coords.longitude - startPoint.longitude) < 0.0001;
      });

      if (matchingHcp) {
        location = getHcpAddress(matchingHcp);
      } else {
        location = startPoint.address || `(${startPoint.longitude}, ${startPoint.latitude})`;
      }
    }

    return {
      latitude: startPoint.latitude,
      longitude: startPoint.longitude,
      location,
    };
  });

  const [form, setForm] = useState(getInitialState);

  const startName = useMemo(() => {
    const defaultName = 'Start Location';
    if (!state.routingResult) return defaultName;

    // Use startingLocation if available (most reliable source)
    if (state.startingLocation) {
      const { startingLocation } = state;

      if (startingLocation.type === 'hcp') {
        const hcpFromResults = hcpResults.find(h => getHcpId(h) === startingLocation.id);
        if (hcpFromResults) {
          const address = getHcpAddress(hcpFromResults);
          const name = getHcpName(hcpFromResults);
          return address ? `${name} - ${address}` : name;
        }
        return undefined;
      }
      if (startingLocation.type === 'search') {
        return startingLocation.name;
      }
      if (startingLocation.type === 'browser') {
        return `Current Location (${startingLocation.latitude}, ${startingLocation.longitude})`;
      }
    }

    // Fallback to route leg data
    const startPoint = state.routingResult.legs[0].start;
    if (startPoint.kolId) {
      const hcp = hcpResults.find(h => getHcpId(h) === startPoint.kolId);
      return hcp ? getHcpName(hcp) : `HCP ${startPoint.kolId}`;
    }

    return startPoint.address || defaultName;
  }, [
    state,
    hcpResults,
  ]);

  const resetForm = useCallback(() => {
    setEditing(false);
    setForm(getInitialState());
  }, [
    getInitialState,
    setEditing,
  ]);

  const canSubmit = useMemo(() => {
    return Object.values(form).some(x => x.id) || !!startLocation.id;
  }, [form, startLocation.id]);

  const handleSubmit = useCallback(() => {
    if (startLocation) {
      dispatch({
        type: 'submit-location',
        payload: {
          startingLocation: {
            type: 'search' as const,
            id: startLocation.id || 'updated-location',
            name: startLocation.location,
            latitude: startLocation.latitude,
            longitude: startLocation.longitude,
          },
        },
      });
      resetForm();
    } else {
      getLocation().then((location) => {
        dispatch({
          type: 'submit-location',
          payload: {
            startingLocation: {
              type: 'browser' as const,
              latitude: location.latitude,
              longitude: location.longitude,
            },
          },
        });
        resetForm();
      });
    }
  }, [dispatch, startLocation, resetForm, getLocation]);

  if (!state.routingResult) {
    return null;
  }

  return (
    <Routes>
      <Header>{copy.header}</Header>
      <Actions>
        {/* {!editing &&
          <EditAction onClick={() => setEditing(true)}>
            <EditIcon size={18} />
          </EditAction>} */}
        {editing &&
          <CancelAction onClick={resetForm}>
            <CancelIcon size={18} />
          </CancelAction>}
        {(editing && canSubmit) &&
          <SubmitAction onClick={handleSubmit}>
            <CheckIcon size={18} />
          </SubmitAction>}
      </Actions>
      <RouteLegs>
        <EditableStartLocation
          name={startName}
          editing={editing}
          startLocation={startLocation}
          setStartLocation={setStartLocation} />
        {
          legs.map((x, i) =>
            <div key={i}>
              {!editing
                ? <RouteLeg {...x} />
                : <EditableRouteLeg {...x} {...form[x.ordinal]} setForm={data => setForm(form => ({ ...form, [x.ordinal]: data }))} />}
            </div>)
        }
      </RouteLegs >
      <Total>
        <Em>{`Total Distance:`}</Em>
        <div>{`${state.routingResult.legs.reduce((acc, x) => acc + x.distanceMiles, 0).toFixed(1)} miles`}</div>
      </Total>
      <Header>{copy.footer}</Header>
    </Routes >
  );
};

type EditableStartLocationProps = {
  editing: boolean;
  startLocation: FormItem;
  setStartLocation: (data: FormItem) => void;
  name: string;
};

const EditableStartLocation = (props: EditableStartLocationProps) => {
  const currentName = props.startLocation.location ?? `(${props.startLocation.longitude}, ${props.startLocation.latitude})`;
  if (props.editing) {
    return (
      <Root>
        <Em>{props.name}</Em>
        <div>
          <SelectLocation
            onChange={val => props.setStartLocation({ ...val, location: val.name, id: 'changed-val' })}
            placeholder={currentName}
            value={{
              latitude: props.startLocation.latitude,
              longitude: props.startLocation.longitude,
              id: props.startLocation.id,
              name: props.startLocation.location,
            }} />
        </div>
      </Root>
    );
  } else {
    return (<Root><Em>{props.name}</Em>{props.name ? '' : currentName}</Root>);
  }
};

const Routes = styled.div({
  position: 'relative',
});

const Actions = styled.div({
  display: 'flex',
  gap: 5,
  position: 'absolute',
  top: 30,
  right: 0,
});

const EditIcon = styled(Edit2)(({ theme }) => `
  color: ${theme.palette.primary.main};
  cursor: pointer;
`);

const EditAction = styled.div(({ theme }) => `
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid ${theme.palette.primary.main};
  border-radius: 50%;
  width: 32px;
  height: 32px;
`);

const CancelIcon = styled(X)(({ theme }) => `
  color: ${theme.palette.black.main};
  cursor: pointer;
`);

const CancelAction = styled.div(({ theme }) => `
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid ${theme.palette.black.main};
  border-radius: 50%;
  width: 32px;
  height: 32px;
`);

const CheckIcon = styled(Check)(({ theme }) => `
  color: ${theme.palette.green.main};
  cursor: pointer;
`);

const SubmitAction = styled.div(({ theme }) => `
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid ${theme.palette.green.main};
  border-radius: 50%;
  width: 32px;
  height: 32px;
`);

const RouteLegs = styled.div({
  margin: '15px 0',
});

const Root = styled.div({
  display: 'flex',
  gap: 5,
  alignItems: 'center',
  marginBottom: 10,
});

const Total = styled.div({
  display: 'flex',
  gap: 5,
});

const Em = styled.div({
  fontFamily: 'var(--font-semibold)',
});

const copy = {
  header: `Based on your selected HCPs we've generated an efficient route to maximize your visits while minimizing travel time.`,
  footer: `This route is optimized to ensure efficient visits while minimizing travel time. You can now proceed with your day seamlessly!`,
};

const Header = styled.div({
  marginBottom: 10,
});

type MapsProps = Pick<Props, 'state'>;

const RouteResultMap = (props: MapsProps) => {
  if (!props.state.routingResult) return null;

  if (props.state.routingResult.provider === LocationProviderType.Azure) {
    return (
      <MapContainer>
        <AzureRouteMap {...props} />
      </MapContainer>
    );
  }
};

const AzureRouteMap = (props: MapsProps) => {
  const dataSourceId = useMemo(() => `route-${cuid2.createId()}`, []);
  const { searchResults: hcpResults } = useContext(SmartRoutingHcpContext);

  const getPointName = useCallback((point: Chat.QueryState.LocationInfo) => {
    if (point.address) {
      return point.address;
    }

    if (point.kolId) {
      const hcp = hcpResults.find(h => getHcpId(h) === point.kolId);
      return hcp ? getHcpName(hcp) : `HCP ${point.kolId}`;
    } else {
      // Try to find matching HCP by coordinates (workaround for missing kolId)
      const matchingHcp = hcpResults.find(h => {
        const coords = getHcpLocationCoords(h);
        return coords &&
          Math.abs(coords.latitude - point.latitude) < 0.0001 &&
          Math.abs(coords.longitude - point.longitude) < 0.0001;
      });

      if (matchingHcp) {
        return getHcpName(matchingHcp);
      }

      return null;
    }
  }, [hcpResults]);

  const features = useMemo(() => {
    if (!props.state.routingResult) return [];

    const routingResult = props.state.routingResult;

    return [
      new atlas.data.MultiLineString(routingResult.legs.map(l => l.meta.points.map(p => [p.longitude, p.latitude]))),
      new atlas.data.Feature(new atlas.data.Point([routingResult.legs[0].start.longitude, routingResult.legs[0].start.latitude]), {
        // @ts-expect-error strictNullChecks are causing issues here across packages
        title: getPointName(routingResult.legs[0].start) ?? 'Start',
        icon: 'pin-round-red',
      }),
      ...routingResult.legs.map(leg => new atlas.data.Feature(new atlas.data.Point([leg.end.longitude, leg.end.latitude]), {
        // @ts-expect-error strictNullChecks are causing issues here across packages
        title: getPointName(leg.end),
        icon: 'pin-round-blue'
      }),
      ),
    ];
  }, [getPointName, props.state.routingResult]);

  const layers = useMemo(() => {
    return [
      new atlas.layer.LineLayer(dataSourceId, 'lines', {
        strokeColor: '#2272B9',
        strokeWidth: 5,
        lineJoin: 'round',
        lineCap: 'round',
      }),
      new atlas.layer.SymbolLayer(dataSourceId, 'icon-symbols', {
        iconOptions: {
          image: ['get', 'icon'],
          allowOverlap: true,
          ignorePlacement: true,
          offset: [0, 0],
        },
        textOptions: {
          textField: ['get', 'title'],
          offset: [0, 1.2],
          haloColor: 'white',
          haloWidth: 1.5,
        },
        filter: ['any', ['==', ['geometry-type'], 'Point'], ['==', ['geometry-type'], 'MultiPoint']], //Only render Point or MultiPoints in this layer.
      }),
    ];
  }, [dataSourceId]);

  if (!props.state.routingResult) return null;

  return (
    <AzureMap features={features} layers={layers} dataSourceId={dataSourceId} height='100%' width='100%' />
  );
};

const MapContainer = styled.div({
  height: 300,
  width: '100%',
  marginTop: 15,
});
